import { NonNullable } from '@pubstack/common/src/assertion'
import { isAdmStack, Stack } from '@pubstack/common/src/stack'
import { groupBy } from 'lodash-es'
import { parseAsString, useQueryState } from 'nuqs'
import { Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Tooltip } from '~/components/Tooltip'
import Table, { TableColumns } from '~/components/table/Table'
import { AdmSitePageContext, isAdmContext, PbsSitePageContext } from '../useSitePageContext'
import { useForm, useWatch } from 'react-hook-form'
import { Input } from '~/components/Input'
import { Select } from '~/components/Select'
import { StackTableRow } from './StackTableRow'
import { isStackNameReserved } from '@pubstack/common/src/stack'
import { ChangePrioritiesModal } from './ChangePrioritiesModal'
import { useToast } from '~/components/Toast/useToasts'
import { toBulkUpdate, useBulkUpdateStacks } from '~/api/stacks.api'
import { StackTemplateFlyout } from '~/modules/pbs/sites/site/stacks/StackTemplateFlyout'
import { Colors } from '~/assets/style/colors'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { EditStackFlyout } from './EditStackFlyout'

export type StackGroup = {
  priority?: number | null
  stackGroupId: string
  stacks: Stack[]
}

const PRIORITY_TOOLTIP = 'If more than one stack matches the context keys on the page, the stack with the highest priority will be used.'

enum STACK_SELECT_FILTER {
  ALL = 'all',
  ACTIVE_ONLY = 'active',
  ARCHIVED_ONLY = 'archived',
}

export const Stacks: FunctionComponent<PbsSitePageContext | AdmSitePageContext> = (props) => {
  // Common props
  const { product, site, isLoading, stacks, bidderRules } = props
  const { contexts } = isAdmContext(props) ? props : { contexts: [] }
  // Staff-only columns still ask the user directly; the role question below has its own hook.
  const user = useAuthenticatedUser()
  /* Creating a stack, archiving one and reordering priorities are the same owner-only call at the API,
     so they read from one place rather than from a role check spelled out again here. */
  const { canEdit: canEditPriorities, disabledReason } = useScopePermissions()
  const modal = useGlobalModal()
  const flyout = useGlobalFlyout()
  const toast = useToast()

  /* `?edit=<stackId>` opens a Node stack's edit flyout over its media's list: how a rule's "Used by" table
     edits one, so that closing the flyout leaves the reader on the stack's media rather than on the rule.
     Read once the stacks are in, then dropped from the URL so a reload does not reopen it. */
  const [stackToEdit, setStackToEdit] = useQueryState('edit', parseAsString)
  // The flyout handler changes once it opens, and the URL drops the id a moment later: once per id.
  const openedForEdit = useRef<string | null>(null)
  useEffect(() => {
    if (product !== 'pbs' || !stackToEdit || isLoading || openedForEdit.current === stackToEdit) return
    openedForEdit.current = stackToEdit
    const stack = stacks.find(({ id }) => id === stackToEdit)
    void setStackToEdit(null)
    if (stack) {
      flyout.open(EditStackFlyout, { color: Colors.King, closeTrackingLabel: 'pbs-sites-stacks-edit-close' }, { siteId: site.id, stack, onClose: () => flyout.close() })
    }
  }, [product, stackToEdit, isLoading, stacks, site.id, flyout, setStackToEdit])

  const [isEditingPriorities, setIsEditingPriorities] = useState(false)
  const [stacksState, setStacksState] = useState(stacks)
  // Resync local copy when the stacks prop changes ("previous value" pattern).
  const [prevStacks, setPrevStacks] = useState(stacks)
  if (stacks !== prevStacks) {
    setPrevStacks(stacks)
    setStacksState(stacks)
  }

  const columns: TableColumns<Stack> = (
    [
      ...(isEditingPriorities
        ? [
            {
              name: '',
              isSortable: false,
            },
          ]
        : []),
      {
        name: (
          <>
            <Tooltip className={'sticky'} title={PRIORITY_TOOLTIP} reposition disabled={product !== 'adm'}>
              #
            </Tooltip>
          </>
        ),
        isSortable: false,
      },
      {
        name: 'Name',
        isSortable: false,
      },
      {
        name: 'Version',
        isSortable: false,
      },
      {
        name: 'Status',
        isSortable: false,
      },
      {
        name: 'Context',
        isSortable: false,
      },
      {
        name: 'Rules',
        isSortable: false,
      },
      product === 'adm'
        ? {
            name: 'Server-side',
            isSortable: false,
          }
        : undefined,
      user?.isAdmin
        ? {
            name: 'Stack Config',
            isSortable: false,
          }
        : undefined,
      {
        name: 'Actions',
        isSortable: false,
      },
    ] as TableColumns
  ).filter(NonNullable)

  // API Hook
  const { mutateAsync: bulkUpdateStacks, isPending: isStackUpdatesPending } = useBulkUpdateStacks()

  const { control } = useForm<{ search: string; filter: STACK_SELECT_FILTER }>({
    defaultValues: {
      search: '',
      filter: STACK_SELECT_FILTER.ACTIVE_ONLY,
    },
  })
  const search = useWatch({ control, name: 'search' })
  const filter = useWatch({ control, name: 'filter' })

  const applyFilter = (stack: Stack) => {
    if (filter === STACK_SELECT_FILTER.ALL) {
      return true
    }
    if (filter === STACK_SELECT_FILTER.ACTIVE_ONLY) {
      return stack.status !== 'archived_group'
    }
    if (filter === STACK_SELECT_FILTER.ARCHIVED_ONLY) {
      return stack.status === 'archived_group'
    }
    return false
  }

  const applySearchAndFilter = (groups: Stack[]) => groups.filter((stack) => stack.name.toLowerCase().includes(search.toLowerCase()) && applyFilter(stack))

  const groupedStacksByGroupId = groupBy(applySearchAndFilter(stacksState || []), 'stackGroupId')
  const groupedStacks: StackGroup[] = Object.entries(groupedStacksByGroupId).map(([stackGroupId, stacks]) => ({
    stackGroupId,
    stacks,
    priority: stacks[0].priority,
  }))

  const maxPriority = useMemo(() => groupedStacks.filter((g) => !g.stacks.some((s) => s.status === 'archived_group' || isStackNameReserved(s.name))).length, [groupedStacks])

  const hasContextAvailable = useMemo(() => {
    const enabledContexts = contexts.filter((c) => c.enabled)
    return enabledContexts.some((context) => !stacks.some((s) => s.configuration.contextId === context.id))
  }, [contexts, stacks])

  const updatePriority = (stackGroupId: string, priority: -1 | 1) => {
    const stackIdIndex = groupedStacks.findIndex((sg) => sg.stackGroupId === stackGroupId)
    const otherIndex = stackIdIndex + priority
    const stackPriority = groupedStacks[stackIdIndex].priority
    const otherPriority = groupedStacks[otherIndex].priority

    const newStackGroups = [...groupedStacks]
    newStackGroups.splice(stackIdIndex, 1, { ...groupedStacks[stackIdIndex], stacks: groupedStacks[stackIdIndex].stacks.map((g) => ({ ...g, priority: otherPriority })), priority: otherPriority })
    newStackGroups.splice(otherIndex, 1, { ...groupedStacks[otherIndex], stacks: groupedStacks[otherIndex].stacks.map((g) => ({ ...g, priority: stackPriority })), priority: stackPriority })

    newStackGroups.sort((a, b) => (a.stacks[0].status === 'archived_group' ? 2 : !a.priority ? 1 : a.priority - (b.priority || 0)))
    setStacksState(newStackGroups.flatMap((n) => n.stacks).filter(isAdmStack))
  }

  const onEditPriorities = () => {
    setIsEditingPriorities(true)
  }

  const onValidatePriorities = async () => {
    try {
      const changedStacksPriorities = stacksState.filter((s) => s.priority !== stacks.find((sS) => sS.id === s.id)?.priority)
      if (changedStacksPriorities.length > 0) {
        // Use the lightweight bulk update (id/status/ratio/priority): a full PUT would re-validate the
        // whole stored snapshot/output, which can legitimately fail validation.
        await bulkUpdateStacks(changedStacksPriorities.map(toBulkUpdate))
        toast.success('Stack priorities have been updated.')
      }

      setIsEditingPriorities(false)
    } catch (_exception) {
      toast.alert(`An error occurred while updating the stack priorities.`)
    }
  }

  const onStackArchive = async (stack: Stack, concurrentStack?: Stack) => {
    if (isStackUpdatesPending) return
    // We archive (archive group) the last version, this one is restorable
    // As the archive button is on the "last" version, we can archive the concurrentStack
    const stacksToArchive: Stack[] = [
      {
        ...stack,
        status: 'archived_group',
        ratio: 0,
        priority: undefined,
      },
      ...(concurrentStack
        ? [
            {
              ...concurrentStack,
              status: 'archived',
              ratio: 0,
            } as Stack,
          ]
        : []),
    ]

    // We must not forget to decrease priorities of other stacks
    const stacksPrioritiesToUpdate = stacks.filter((s) => !!s.priority && s.priority > (stack.priority ?? 0)).map((s) => ({ ...s, priority: (s.priority ?? 0) - 1 }))

    try {
      await bulkUpdateStacks([...stacksToArchive, ...stacksPrioritiesToUpdate].map(toBulkUpdate))
      toast.success(
        <>
          <strong>{stack.name}</strong> has been archived.
        </>
      )
    } catch (_e) {
      toast.alert(`An error occurred while archiving the stack.`)
    }
  }

  return (
    <div className={'flex flex-col gap-4'}>
      {isEditingPriorities && <div>Order the stacks by priority from top (high priority) to bottom (low).</div>}
      {product === 'adm' && (
        <div className={'flex items-center gap-0.5'}>
          <form className={'inline-flex shrink-0 grow items-center gap-3'}>
            <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} disabled={isEditingPriorities} />
            <Select
              label={'Filter'}
              labelIsPlaceholder
              name={'filter'}
              control={control}
              options={[
                { value: STACK_SELECT_FILTER.ALL, label: 'All', trackingLabel: 'adm-sites-stacks-select-all-apply' },
                { value: STACK_SELECT_FILTER.ACTIVE_ONLY, label: 'Active stacks', trackingLabel: 'adm-sites-stacks-select-active-apply' },
                {
                  value: STACK_SELECT_FILTER.ARCHIVED_ONLY,
                  label: 'Archived stacks',
                  disabled: stacks.every((s) => s.status !== 'archived_group'),
                  trackingLabel: 'adm-sites-stacks-select-archived-apply',
                },
              ]}
              disabled={isEditingPriorities}
            />
          </form>
          {/* Each button keeps the left margin the bar's former `Button` selector gave it. */}
          <form>
            {canEditPriorities &&
              (!isEditingPriorities ? (
                <Tooltip title={PRIORITY_TOOLTIP} reposition>
                  <Button
                    className={'ml-4'}
                    variant={'secondary'}
                    onClick={onEditPriorities}
                    disabled={
                      !!search || filter === STACK_SELECT_FILTER.ARCHIVED_ONLY || stacksState.filter((stack) => !isStackNameReserved(stack.name) || stack.status === 'archived_group').length < 2
                    }
                    trackingLabel={'adm-sites-stacks-priorities-edit'}
                  >
                    Edit priorities
                  </Button>
                </Tooltip>
              ) : (
                <>
                  <Button
                    className={'ml-4'}
                    variant={'tertiary'}
                    onClick={() => {
                      setIsEditingPriorities(false)
                      setStacksState(stacks)
                    }}
                    trackingLabel={'adm-sites-stacks-priorities-cancel'}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={'ml-4'}
                    variant={'primary'}
                    onClick={(event) => {
                      event.stopPropagation()
                      modal.open(ChangePrioritiesModal, {
                        onValidate: async () => {
                          await onValidatePriorities()
                          modal.close()
                        },
                      })
                    }}
                    trackingLabel={'adm-sites-stacks-priorities-save'}
                    iconName={isStackUpdatesPending ? 'pubstack' : undefined}
                    iconSpin={isStackUpdatesPending}
                    disabled={isStackUpdatesPending}
                  >
                    Validate priorities
                  </Button>
                </>
              ))}
            {!groupedStacks.some((g) => g.stacks.some((s) => isStackNameReserved(s.name) && s.status === 'live')) ? (
              <Tooltip title={'Deploy Default before creating another stack'} align={'end'}>
                <Button className={'ml-4'} variant={'primary'} disabled trackingLabel={'ana-sites-stacks-new'}>
                  New stack
                </Button>
              </Tooltip>
            ) : (
              /* Creating a stack is owner-only at the API — the button stays on screen so a reader knows
                 the capability exists and who to ask for it. */
              <Tooltip title={disabledReason} disabled={canEditPriorities} align={'end'}>
                <Button
                  className={'ml-4'}
                  variant={'primary'}
                  onClick={(event) => {
                    event.stopPropagation()
                    if (hasContextAvailable) {
                      flyout.open(
                        StackTemplateFlyout,
                        { color: Colors.Lilac, width: `816px`, closeTrackingLabel: 'adm-sites-stacks-template-close' },
                        {
                          currentSite: site,
                          closeFlyout: () => flyout.close(),
                        }
                      )
                    } else {
                    }
                  }}
                  disabled={!canEditPriorities || isEditingPriorities || isLoading}
                  trackingLabel={'adm-sites-stacks-new'}
                >
                  New stack
                </Button>
              </Tooltip>
            )}
          </form>
        </div>
      )}
      {isLoading || stacks.length !== 0 ? (
        <Table columns={columns} isLoading={isLoading}>
          {groupedStacks.map(({ stackGroupId, stacks: stacksFromGroup }) => (
            <Fragment key={stackGroupId}>
              {stacksFromGroup.map((stack, index) => {
                const concurrentStack = stacksFromGroup.find((s) => s.version !== stack.version)
                return (
                  <StackTableRow
                    key={stack.id}
                    product={product}
                    site={site}
                    stack={stack}
                    concurrentStack={concurrentStack}
                    isFirst={index === 0}
                    isLast={index === stacksFromGroup.length - 1}
                    bidderRules={bidderRules}
                    showStackConfigColumn={user?.isAdmin}
                    contexts={'contexts' in props ? props.contexts : []}
                    isEditingPriorities={isEditingPriorities}
                    maxPriority={maxPriority}
                    updatePriority={updatePriority}
                    onStackArchive={onStackArchive}
                  />
                )
              })}
            </Fragment>
          ))}
        </Table>
      ) : (
        <IllustrationMessage iconName="night_sky" iconSize="190px" title="It seems that no stack has been created."></IllustrationMessage>
      )}
    </div>
  )
}

export const PbsStacks: FunctionComponent = () => {
  const context = useOutletContext<PbsSitePageContext>()
  return <Stacks {...context} />
}

export const AdmStacks: FunctionComponent = () => {
  const context = useOutletContext<AdmSitePageContext>()
  return <Stacks {...context} />
}
